import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import {
  ChevronLeft, Copy, Check, ExternalLink, BadgeCheck, MapPin,
  Sparkles, Users2, CalendarClock, Image as ImageIcon,
  Save, Pencil, Video, TrendingUp, Camera, Building2,
} from "lucide-react";
import * as Icons from "lucide-react";
import {
  AnnouncementWall, StudioMomentsStrip,
} from "@/components/SocialFeed";

/* Agency public profile — org-persona palettes · Feb 2026.
 *
 *  Two routes hit this file:
 *    · /office/public-profile   → owner edit (auth)
 *    · /agency/:handle          → public view (no auth)
 *
 *  Design language mirrors the individual (UserPublicProfile) —
 *  cover band ▸ overlapping hero card ▸ statement ▸ showreel ▸
 *  rosters ▸ owner editor. Palette diverges **per org persona**:
 *
 *    · Individual (crew/talent)   → cinema-dark, gold, punchy.
 *    · Advertising Agency         → warm cream paper, oxblood ink,
 *                                    italic serif statement.
 *                                    (Ogilvy / W+K / Chiat-Day feel.)
 *    · Production House / Studio  → deep near-black, magenta-and-
 *                                    cyan **sound-stage neon** gels.
 *                                    Reads soundstage / LED wall.
 *
 *  Palette + typography constants live at the top so a future
 *  "customise your profile theme" feature (per user's plan) can
 *  simply override this object without touching layout.
 */
const AGENCY_THEME = {
  name:     "agency",
  bg:       "#F5F1E8",   // warm cream — expensive-paper feel
  surface:  "#FFFFFF",   // white cards over cream
  ink:      "#1E1B18",   // near-black warm charcoal
  muted:    "#6E6659",   // muted secondary text
  line:     "#E4DFD3",   // borders / dividers
  chipBg:   "#EEEAE0",   // chip / tag background
  accent:   "#8B1A1A",   // oxblood — Ogilvy red / classic advertising
  accentSoft: "#8B1A1A22",
  gold:     "#C29A38",   // secondary accent for awards / verified
  ctaText:  "#F8F1EA",   // text on the accent CTA
  onDark:   false,
  // Cover fallback — warm paper gradient with grain + diagonal ruled lines
  coverGradient:
    "radial-gradient(140% 90% at 15% 10%, #EFE4C6 0%, #E7D8B2 30%, #DCC994 55%, #C7AF74 100%)",
  coverGrainBlend: "multiply",
  coverGrainOpacity: 0.09,
  showRuledLines:  true,
  statementFont:   "font-serif italic",
  brandMarkBg:     "#EEEAE0",
  inputBg:         "#FDFBF6",
};

// "Sound-stage neon" — deep near-black + magenta + cyan + sodium yellow.
// This palette is deliberately punchier than the crew's cinema-dark
// (which stays warm-gold). Reads big-budget production house / studio.
const PRODUCTION_THEME = {
  name:     "production",
  bg:       "#0A0812",         // deep aubergine-black
  surface:  "#151021",         // card panel — a touch lifted
  ink:      "#F7F5F2",         // off-white text
  muted:    "#8B8598",
  line:     "rgba(255,255,255,0.10)",
  chipBg:   "rgba(255,255,255,0.05)",
  accent:   "#FF2E63",         // hot magenta — primary neon gel
  accentSoft: "rgba(255,46,99,0.14)",
  gold:     "#FFD447",         // sodium yellow — awards/verified
  ctaText:  "#0A0812",         // dark text on hot-pink CTA reads best
  onDark:   true,
  // Cover fallback — full-bleed cinematic gel gradient · magenta ↔ cyan
  coverGradient:
    "radial-gradient(80% 100% at 15% 0%, #6B1246 0%, transparent 55%), " +
    "radial-gradient(90% 90% at 100% 100%, #003D66 0%, transparent 55%), " +
    "linear-gradient(180deg, #0F0819 0%, #0A0812 100%)",
  coverGrainBlend: "screen",
  coverGrainOpacity: 0.10,
  showRuledLines:  false,
  statementFont:   "font-display",   // display sans, no italic
  brandMarkBg:     "rgba(255,255,255,0.06)",
  inputBg:         "#1B1526",
};


/** Pick the palette the given org profile should render in.
 *  `type` values with `advertising_agency`, `talent_model`, `pr_agency`
 *  → cream/oxblood. Everything else (production_house, studio,
 *  vfx_studio, post_studio, and unknowns) → sound-stage neon. */
function _themeFor(profile) {
  const t = (profile?.type || "").toLowerCase();
  const CREAM_TYPES = new Set([
    "advertising_agency", "creative_agency", "digital_agency",
    "pr_agency", "media_agency", "influencer_agency",
    "talent_model", "casting_agency", "talent_management",
  ]);
  return CREAM_TYPES.has(t) ? AGENCY_THEME : PRODUCTION_THEME;
}


export default function AgencyPublicProfile({ mode = "public" }) {
  const params  = useParams();
  const handle  = params.handle;
  const isOwner = mode === "owner";

  const [data, setData]     = useState(null);
  const [types, setTypes]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isOwner) {
        const [{ data: p }, { data: t }] = await Promise.all([
          api.get("/orgs/mine/public-profile"),
          api.get("/meta/agency-types"),
        ]);
        const { data: pub } = await api.get(`/public/@${p.public_handle}`).catch(() => ({ data: {} }));
        setData({ profile: p, creators: pub?.creators || [],
                   sites: pub?.sites || [], content: pub?.content || [] });
        setTypes(t);
      } else {
        const { data: pub } = await api.get(`/public/@${handle}`);
        setData(pub);
        try {
          const { data: t } = await api.get("/meta/agency-types");
          setTypes(t);
        } catch { /* anon */ }
        api.post("/public/profile-view", {
          handle,
          referrer: document.referrer || "",
          path:     window.location.pathname,
        }).catch(() => { /* silent */ });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load profile");
    } finally { setLoading(false); }
  }, [isOwner, handle]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    // Use a neutral splash instead of committing to either palette —
    // avoids the brief cream-flash on production_house handles.
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-400">
        <div className="mx-auto max-w-4xl px-6 py-16 text-center">Loading…</div>
      </div>
    );
  }

  const p = data.profile;
  const t = _themeFor(p);
  const pickedTypes = (types?.types || []).filter((tp) => (p.agency_types || []).includes(tp.key));
  const totalRoster = (data.sites?.length || 0) + (data.creators?.length || 0) + (data.content?.length || 0);

  return (
    <div className="min-h-screen"
         style={{ background: t.bg, color: t.ink }}
         data-testid={isOwner ? "agency-public-profile-owner"
                              : "agency-public-profile-view"}>

      {isOwner && (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-6">
          <Link to="/dashboard"
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase transition"
                style={{ color: t.muted }}>
            <ChevronLeft size={12} /> Back to Office
          </Link>
        </div>
      )}

      <CoverBand profile={p} isOwner={isOwner} onChanged={load} t={t} />
      <HeroCard  profile={p} isOwner={isOwner} pickedTypes={pickedTypes} t={t} />
      <StatementBlock profile={p} t={t} />
      <ShowreelStrip  profile={p} t={t} />

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-20">
        <StudioMomentsStrip
          isAdmin={isOwner}
          t={t}
          publicListUrl={`/public/orgs/${p.public_handle}/moments`}
          ownerListUrl="/orgs/mine/moments"
          createUrl="/orgs/mine/moments"
          deleteUrl={(mid) => `/orgs/mine/moments/${mid}`}
          photoUrlFor={(m) => `${API.replace(/\/api$/, "")}${m.photo_url}`}
          title="Studio Moments"
          eyebrow="Now Shooting"
          emptyLabel={isOwner
            ? "Post a behind-the-scenes photo — it'll show up here."
            : "This studio hasn't shared any moments yet."}
          testidPrefix="agency-moments"
        />

        <AnnouncementWall
          isAdmin={isOwner}
          t={t}
          listUrl={`/public/orgs/${p.public_handle}/announcements`}
          createUrl="/orgs/mine/announcements"
          deleteUrl={(aid) => `/orgs/mine/announcements/${aid}`}
          reactUrl={(aid) => `/orgs/announcements/${aid}/reactions`}
          photoUrlFor={(post) => post.photo_url
            ? `${API.replace(/\/api$/, "")}${post.photo_url}`
            : ""}
          title="The Wall"
          eyebrow="Announcements"
          emptyLabel={isOwner
            ? "Share your first update — casting calls, wrap-day recaps, festival wins."
            : "This studio hasn't posted any announcements yet."}
          testidPrefix="agency-wall"
        />

        {data.sites?.length > 0 && <SiteRoster sites={data.sites} t={t} />}
        {data.creators?.length > 0 && <CreatorRoster creators={data.creators} t={t} />}
        {data.content?.length > 0 && <ContentReel content={data.content} t={t} />}
        {totalRoster === 0 && !isOwner && (
          <div className="mt-12 rounded-2xl p-10 text-center"
               style={{ background: t.surface,
                        border: `1px solid ${t.line}` }}>
            <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: t.muted }}>
              Coming soon
            </div>
            <div className="mt-2 font-display text-xl uppercase tracking-tight">
              {p.brand_name} hasn&apos;t published a public roster yet
            </div>
            <p className="mt-2 text-sm max-w-lg mx-auto" style={{ color: t.muted }}>
              Check back soon — creators, OOH sites, and content pieces will
              show up here as items are marked public.
            </p>
          </div>
        )}

        {isOwner && (
          <div className="mt-12 space-y-6">
            <AnalyticsWidget t={t} />
            <OwnerEditor profile={p} onSaved={load} t={t} />
          </div>
        )}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  Cover band — warm paper gradient fallback, subtle grain.
 * ═══════════════════════════════════════════════════════════════════ */
function CoverBand({ profile, isOwner, onChanged, t }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      // Backwards-compat: orgs profile PATCH takes public_hero_image_url as
      // a URL, but a proper upload endpoint may not exist yet. Fall back
      // to `readAsDataURL` (base64) + PATCH so cover shows up immediately.
      // A dedicated `/orgs/mine/cover-photo` endpoint is a wave-2 nicety.
      const reader = new FileReader();
      reader.readAsDataURL(f);
      await new Promise((res) => { reader.onloadend = res; });
      await api.patch("/orgs/mine/public-profile",
        { public_hero_image_url: reader.result });
      toast.success("Cover photo updated");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const src = profile.public_hero_image_url || "";

  return (
    <div className="relative" data-testid="app-cover-band">
      <div className="relative h-48 sm:h-64 lg:h-72 overflow-hidden">
        {src ? (
          <img src={src} alt=""
               className="w-full h-full object-cover"
               onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div className="absolute inset-0" style={{ background: t.coverGradient }}>
            {/* Subtle grain — SVG turbulence blended per theme */}
            <div className="absolute inset-0"
                 style={{
                   opacity: t.coverGrainOpacity,
                   mixBlendMode: t.coverGrainBlend,
                   backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"200\" height=\"200\"><filter id=\"n\"><feTurbulence type=\"fractalNoise\" baseFrequency=\"0.85\" numOctaves=\"2\"/></filter><rect width=\"100%25\" height=\"100%25\" filter=\"url(%23n)\"/></svg>')",
                 }} />
            {/* Faint diagonal ruled lines — an atelier flourish (agency only) */}
            {t.showRuledLines && (
              <div className="absolute inset-0 opacity-[0.05]"
                   style={{
                     backgroundImage: "repeating-linear-gradient(45deg, #1E1B18 0 1px, transparent 1px 24px)",
                   }} />
            )}
            {/* Production theme adds a subtle vignette + accent glow */}
            {t.name === "production" && (
              <>
                <div className="absolute inset-0 pointer-events-none"
                     style={{
                       background: "radial-gradient(120% 100% at 50% 50%, transparent 50%, rgba(0,0,0,0.55) 100%)",
                     }} />
                <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
                     style={{
                       background: `linear-gradient(to top, ${t.accent}18, transparent)`,
                     }} />
              </>
            )}
          </div>
        )}
        {/* Bottom fade into page bg so the hero card blends cleanly */}
        <div className="absolute inset-x-0 bottom-0 h-16"
             style={{ background: `linear-gradient(to bottom, transparent, ${t.bg})` }} />

        {isOwner && (
          <div className="absolute top-4 right-4">
            <input ref={fileRef} type="file"
                   accept="image/png,image/jpeg,image/webp"
                   className="hidden"
                   onChange={(e) => { const f = e.target.files?.[0]; upload(f); e.target.value = ""; }}
                   data-testid="app-cover-input" />
            <button type="button" disabled={busy}
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold rounded-full px-3 py-1.5 transition-all disabled:opacity-40 backdrop-blur-md"
                    style={t.onDark
                      ? { background: "rgba(10,8,18,0.65)", color: t.ink,
                          border: `1px solid ${t.line}` }
                      : { background: "rgba(255,255,255,0.9)", color: t.ink,
                          border: `1px solid ${t.line}` }}
                    data-testid="app-cover-upload">
              <Camera size={11} />
              {busy ? "Uploading…" : src ? "Replace cover" : "Add cover"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  Hero card — overlaps cover by -60px. Brand mark left, name right,
 *  contextual CTA far right ("Enquire" or "Watch reel").
 * ═══════════════════════════════════════════════════════════════════ */
function HeroCard({ profile, isOwner, pickedTypes, t }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/agency/${profile.public_handle}`;
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  };

  const primaryCta = _primaryCta(profile);
  const logoRef = profile.logo_url
                || profile.callsheet_branding?.primary_logo_ref
                || "";

  const shadowRgba = t.onDark ? "rgba(0,0,0,0.55)" : "rgba(30,27,24,0.18)";
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 -mt-14 sm:-mt-16 relative z-10">
      <div className="rounded-3xl px-5 sm:px-8 py-6 sm:py-7"
           style={{ background: t.surface,
                    border: `1px solid ${t.line}`,
                    boxShadow: `0 22px 60px -20px ${shadowRgba}` }}
           data-testid="app-hero-card">
        <div className="flex flex-col sm:flex-row sm:items-end gap-5">
          {/* Brand mark (agency logo) */}
          <div className="shrink-0 -mt-14 sm:-mt-20">
            <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl overflow-hidden flex items-center justify-center"
                 style={{ background: t.brandMarkBg,
                          border: `1px solid ${t.line}`,
                          boxShadow: `0 8px 24px -10px ${shadowRgba}` }}
                 data-testid="app-brand-mark">
              {logoRef ? (
                <img src={logoRef} alt="" className="w-full h-full object-contain p-3"
                     onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <div className="text-center">
                  <Building2 size={26} style={{ color: t.muted }} />
                  <div className="mt-1 font-display text-[9px] tracking-[0.3em] uppercase"
                       style={{ color: t.muted }}>
                    Mark
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Name + meta */}
          <div className="min-w-0 flex-1">
            <div className="text-[10px] tracking-[0.32em] uppercase"
                 style={{ color: t.accent }}
                 data-testid="app-handle">
              @{profile.public_handle}
            </div>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95] mt-1"
                style={{ color: t.ink }}
                data-testid="app-brand">
              {profile.brand_name}
            </h1>
            <div className="mt-2 flex items-center gap-3 flex-wrap text-[12px]"
                 style={{ color: t.muted }}>
              {profile.legal_name && <span>{profile.legal_name}</span>}
              {profile.verified && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] tracking-[0.22em] uppercase font-semibold"
                      style={{ background: `${t.gold}22`,
                               color: t.gold }}>
                  <BadgeCheck size={10} /> Verified
                </span>
              )}
              {profile.office_address_label && (
                <span className="inline-flex items-center gap-1">
                  <MapPin size={11} /> {profile.office_address_label}
                </span>
              )}
            </div>

            {pickedTypes.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="app-types">
                {pickedTypes.slice(0, 5).map((pt) => {
                  const Icon = Icons[pt.icon] || Sparkles;
                  return (
                    <span key={pt.key}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-[0.18em] uppercase font-semibold"
                          style={{ background: t.chipBg,
                                   color: t.ink,
                                   border: `1px solid ${t.line}` }}
                          data-testid={`app-type-${pt.key}`}>
                      <Icon size={11} style={{ color: pt.accent || t.accent }} />
                      {pt.label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Action rail */}
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {primaryCta && (
              <a href={primaryCta.href}
                 target={primaryCta.external ? "_blank" : undefined}
                 rel={primaryCta.external ? "noopener noreferrer" : undefined}
                 className="h-10 px-5 rounded-full inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-semibold transition-colors"
                 style={{ background: t.accent, color: t.ctaText }}
                 onMouseOver={(e) => { e.currentTarget.style.background = t.ink; e.currentTarget.style.color = t.bg; }}
                 onMouseOut={(e)  => { e.currentTarget.style.background = t.accent; e.currentTarget.style.color = t.ctaText; }}
                 data-testid="app-primary-cta">
                {primaryCta.icon}
                {primaryCta.label}
              </a>
            )}
            <button type="button" onClick={copy}
                    className="h-10 w-10 rounded-full inline-flex items-center justify-center transition-colors"
                    style={{ border: `1px solid ${t.line}`,
                             color: t.muted }}
                    aria-label="Copy profile URL"
                    data-testid="app-copy-btn">
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function _primaryCta(p) {
  if (p.public_hero_video_url) {
    return { label: "Watch reel", icon: <Video size={12} />,
             href: p.public_hero_video_url, external: true };
  }
  if (p.public_showreel_urls?.length) {
    return { label: "See work", icon: <Video size={12} />,
             href: p.public_showreel_urls[0], external: true };
  }
  return { label: "Enquire", icon: <ExternalLink size={12} />,
           href: `/agency/${p.public_handle}#contact`, external: false };
}


/* ═══════════════════════════════════════════════════════════════════
 *  Statement — big italic serif pull quote. Only shown when set.
 *  Deliberately styled to feel like the intro pages of a portfolio
 *  book, not a bio blurb.
 * ═══════════════════════════════════════════════════════════════════ */
function StatementBlock({ profile, t }) {
  if (!profile.public_statement) return null;
  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 mt-10"
             data-testid="app-statement-block">
      <div className="max-w-4xl">
        <div className="text-[10px] tracking-[0.32em] uppercase mb-4"
             style={{ color: t.accent }}>
          Our Statement
        </div>
        <p className={`text-xl sm:text-2xl lg:text-3xl leading-[1.35] ${t.statementFont}`}
           style={{ color: t.ink }}
           data-testid="app-statement">
          &ldquo;{profile.public_statement}&rdquo;
        </p>
      </div>
    </section>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  Showreel strip — sits below the statement. Matches the individual
 *  profile's reel grid but with cream cards + ink accents.
 * ═══════════════════════════════════════════════════════════════════ */
function ShowreelStrip({ profile, t }) {
  const reels = profile.public_showreel_urls || [];
  if (!reels.length) return null;
  const inverted = t.onDark;
  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 mt-14"
             data-testid="app-showreel">
      <div className="text-[10px] tracking-[0.32em] uppercase"
           style={{ color: t.accent }}>
        Selected work · links
      </div>
      <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tight mt-1">
        Showreel
      </h2>
      <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
        {reels.map((u, i) => (
          <a key={u} href={u} target="_blank" rel="noreferrer"
             className="group relative aspect-[16/9] rounded-xl overflow-hidden flex items-center justify-center transition-all"
             style={{ background: t.chipBg,
                      border: `1px solid ${t.line}`,
                      color: t.ink }}
             onMouseOver={(e) => {
               e.currentTarget.style.background = inverted ? t.accent : t.ink;
               e.currentTarget.style.color = inverted ? t.ctaText : t.bg;
               e.currentTarget.style.borderColor = inverted ? t.accent : t.ink;
             }}
             onMouseOut={(e)  => {
               e.currentTarget.style.background = t.chipBg;
               e.currentTarget.style.color = t.ink;
               e.currentTarget.style.borderColor = t.line;
             }}
             data-testid={`app-showreel-${i}`}>
            <Video size={26} className="transition-colors" />
            <div className="absolute bottom-2 left-2 right-2 text-[10.5px] tracking-[0.22em] uppercase">
              Reel {i + 1}
            </div>
            <ExternalLink size={12} className="absolute top-2 right-2 opacity-60" />
          </a>
        ))}
      </div>
    </section>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  Roster sections — restyled for the cream palette.
 * ═══════════════════════════════════════════════════════════════════ */
function Section({ title, subtitle, children, testid, t }) {
  return (
    <section className="mt-14" data-testid={testid}>
      <div className="text-[10px] tracking-[0.32em] uppercase"
           style={{ color: t.accent }}>{subtitle}</div>
      <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tight mt-1"
          style={{ color: t.ink }}>{title}</h2>
      <div className="mt-6">{children}</div>
    </section>
  );
}


function AgencyCard({ children, testid, hover = true, className = "", t }) {
  const shadowRgba = t.onDark ? "rgba(0,0,0,0.55)" : "rgba(30,27,24,0.18)";
  return (
    <div data-testid={testid}
         className={"rounded-xl overflow-hidden transition-all " +
                     (hover ? "hover:-translate-y-0.5" : "") + " " + className}
         style={{ background: t.surface,
                  border: `1px solid ${t.line}`,
                  boxShadow: `0 4px 20px -12px ${shadowRgba}` }}>
      {children}
    </div>
  );
}


function SiteRoster({ sites, t }) {
  return (
    <Section title="OOH Site Inventory" subtitle="Available media · book directly"
             testid="app-sites-section" t={t}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sites.map((s) => {
          const d = s.data || {};
          const upcoming = (s.bookings || []).slice(0, 3);
          return (
            <AgencyCard key={s.id} testid={`app-site-${s.id}`} t={t}>
              {d.photo_url && (
                <img src={d.photo_url} alt="" className="w-full h-40 object-cover"
                     onError={(e) => { e.target.style.display = "none"; }} />
              )}
              <div className="p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-base uppercase tracking-tight truncate">{d.site_name || s.title}</div>
                  <span className="px-2 py-0.5 rounded-full text-[9.5px] tracking-[0.22em] uppercase font-semibold"
                        style={{
                          background: s.status === "booked" ? "#F0BE4F22"
                                    : s.status === "available" ? "#2F7A5E22"
                                    : t.chipBg,
                          color:      s.status === "booked" ? "#8A6716"
                                    : s.status === "available" ? "#2F7A5E"
                                    : t.muted,
                        }}>
                    {s.status}
                  </span>
                </div>
                <div className="mt-1 text-[11.5px]" style={{ color: t.muted }}>
                  <MapPin size={11} className="inline mr-0.5" /> {d.city}{d.locality ? `, ${d.locality}` : ""}
                  {d.medium_type && <> · {d.medium_type.replace(/_/g, " ")}</>}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-3 text-[11px]"
                     style={{ color: t.muted }}>
                  {d.size && <span>{d.size}</span>}
                  {d.impressions && <span>{Number(d.impressions).toLocaleString("en-IN")}/mo imps</span>}
                  {d.rate_month && <span style={{ color: t.ink, fontWeight: 600 }}>
                    ₹{Number(d.rate_month).toLocaleString("en-IN")}/mo
                  </span>}
                </div>
                {upcoming.length > 0 && (
                  <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${t.line}` }}>
                    <div className="text-[9.5px] tracking-[0.22em] uppercase font-semibold"
                         style={{ color: t.muted }}>
                      <CalendarClock size={10} className="inline mr-0.5" /> Booked
                    </div>
                    <ul className="mt-1 space-y-0.5">
                      {upcoming.map((b) => (
                        <li key={b.id} className="text-[10.5px] tabular-nums"
                            style={{ color: t.muted }}>
                          {b.from_date} → {b.to_date} ·{" "}
                          <span style={{ color: t.ink }}>{b.client_name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </AgencyCard>
          );
        })}
      </div>
    </Section>
  );
}


function CreatorRoster({ creators, t }) {
  return (
    <Section title="Creator Roster" subtitle="Talent signed with us"
             testid="app-creators-section" t={t}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {creators.map((c) => {
          const d = c.data || {};
          return (
            <AgencyCard key={c.id} testid={`app-creator-${c.id}`}
                        className="p-4 flex items-center gap-3" t={t}>
              {d.avatar_url ? (
                <img src={d.avatar_url} alt="" className="w-14 h-14 rounded-full object-cover"
                     onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <div className="w-14 h-14 rounded-full flex items-center justify-center"
                     style={{ background: t.chipBg,
                              color: t.muted }}>
                  <Users2 size={20} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-display text-base uppercase tracking-tight truncate"
                     style={{ color: t.ink }}>
                  {d.creator_name || c.title}
                </div>
                <div className="text-[11.5px] truncate" style={{ color: t.muted }}>
                  {d.handle} {d.platform && `· ${d.platform}`}
                </div>
                {d.followers && (
                  <div className="text-[10.5px] tabular-nums" style={{ color: t.muted }}>
                    {Number(d.followers).toLocaleString("en-IN")} followers
                  </div>
                )}
                {d.categories && (
                  <div className="mt-1 text-[10px] tracking-[0.15em] uppercase"
                       style={{ color: t.accent }}>
                    {d.categories}
                  </div>
                )}
              </div>
            </AgencyCard>
          );
        })}
      </div>
    </Section>
  );
}


function ContentReel({ content, t }) {
  return (
    <Section title="Content Studio" subtitle="Recent work" testid="app-content-section" t={t}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {content.map((c) => {
          const d = c.data || {};
          const url = d.media_url || "#";
          return (
            <a key={c.id} href={url} target="_blank" rel="noreferrer"
               className="block group">
              <AgencyCard testid={`app-content-${c.id}`} t={t}>
                {d.thumb_url ? (
                  <img src={d.thumb_url} alt="" className="w-full h-40 object-cover"
                       onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <div className="w-full h-40 flex items-center justify-center"
                       style={{ background: t.chipBg,
                                color: t.muted }}>
                    <ImageIcon size={22} />
                  </div>
                )}
                <div className="p-3">
                  <div className="font-display text-sm uppercase tracking-tight truncate">
                    {d.title || c.title}
                  </div>
                  <div className="mt-0.5 text-[10.5px] truncate"
                       style={{ color: t.muted }}>
                    {d.brand_name} {d.format && `· ${d.format.replace(/_/g, " ")}`}
                  </div>
                </div>
              </AgencyCard>
            </a>
          );
        })}
      </div>
    </Section>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  Analytics widget (owner-only) — re-themed for cream.
 * ═══════════════════════════════════════════════════════════════════ */
function AnalyticsWidget({ t }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/orgs/mine/profile-analytics?days=${days}`)
       .then(({ data: d }) => setData(d))
       .catch(() => setData({ total: 0, series: [], referrers: [] }));
  }, [days]);
  if (!data) return null;
  const maxV = Math.max(1, ...data.series.map((d) => d.count));

  const REF_COLORS = {
    whatsapp: "#25D366", linkedin: "#0A66C2", instagram: "#E4405F",
    google:   "#4285F4", direct:   t.muted, other: t.muted,
  };
  const shadowRgba = t.onDark ? "rgba(0,0,0,0.55)" : "rgba(30,27,24,0.15)";

  return (
    <div className="rounded-2xl p-5"
         style={{ background: t.surface,
                  border: `1px solid ${t.line}`,
                  boxShadow: `0 4px 20px -12px ${shadowRgba}` }}
         data-testid="app-analytics-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase inline-flex items-center gap-1.5"
               style={{ color: t.accent }}>
            <TrendingUp size={11} /> Visitor Analytics · admin-only
          </div>
          <div className="mt-1 font-display text-2xl uppercase tracking-tight"
               style={{ color: t.ink }}>
            {data.total} view{data.total === 1 ? "" : "s"}
          </div>
          <div className="mt-0.5 text-[11px]" style={{ color: t.muted }}>
            Anonymous. IPs hashed. Only you see this.
          </div>
        </div>
        <div className="flex items-center gap-1" data-testid="app-analytics-range">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className="px-3 h-8 rounded-full text-[10.5px] tracking-[0.18em] uppercase font-semibold transition"
              style={days === d
                ? { background: t.ink, color: t.bg }
                : { border: `1px solid ${t.line}`, color: t.muted }}
              data-testid={`app-analytics-range-${d}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-end gap-1 h-24" data-testid="app-analytics-sparkline">
        {data.series.map((d, i) => (
          <div key={i} className="flex-1 min-w-0 flex flex-col justify-end items-center"
               title={`${d.date} · ${d.count}`}>
            <div className="w-full rounded-t-sm transition-all"
              style={{ height: `${(d.count / maxV) * 100}%`,
                       minHeight: d.count ? "3px" : "0",
                       background: d.count ? t.gold : "transparent" }} />
          </div>
        ))}
      </div>
      <div className="text-[9px] tracking-[0.2em] uppercase flex justify-between mt-1"
           style={{ color: t.muted }}>
        <span>{data.series[0]?.date?.slice(5)}</span>
        <span>{data.series[data.series.length - 1]?.date?.slice(5)}</span>
      </div>

      {data.referrers.length > 0 && (
        <div className="mt-6 pt-5" style={{ borderTop: `1px solid ${t.line}` }}>
          <div className="text-[10px] tracking-[0.22em] uppercase mb-3"
               style={{ color: t.muted }}>
            Where they came from
          </div>
          <div className="space-y-2">
            {data.referrers.map((r) => {
              const pct = data.total ? Math.round((r.count / data.total) * 100) : 0;
              return (
                <div key={r.bucket} className="flex items-center gap-3"
                     data-testid={`app-analytics-ref-${r.bucket}`}>
                  <div className="w-24 flex-none text-[11px] capitalize"
                       style={{ color: t.ink }}>{r.bucket}</div>
                  <div className="flex-1 h-2 rounded-full overflow-hidden"
                       style={{ background: t.chipBg }}>
                    <div className="h-full rounded-full transition-all"
                         style={{ width: `${pct}%`,
                                  background: REF_COLORS[r.bucket] || t.muted }} />
                  </div>
                  <div className="w-16 text-right text-[11px] tabular-nums"
                       style={{ color: t.ink }}>
                    {r.count} <span style={{ color: t.muted }}>· {pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  Owner editor — re-themed for cream palette. Same behaviour as before.
 * ═══════════════════════════════════════════════════════════════════ */
function OwnerEditor({ profile, onSaved, t }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    public_statement:      profile.public_statement || "",
    public_hero_image_url: profile.public_hero_image_url || "",
    public_hero_video_url: profile.public_hero_video_url || "",
    public_showreel_urls:  (profile.public_showreel_urls || []).join("\n"),
    public_handle:         profile.public_handle || "",
  });
  const [busy, setBusy]       = useState(false);
  const [copied, setCopied]   = useState(false);
  const handleLocked = !!profile.public_handle_locked;
  const publicUrl = `${window.location.origin}/agency/${profile.public_handle}`;

  const copy = async () => {
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        public_statement: form.public_statement,
        public_hero_image_url: form.public_hero_image_url,
        public_hero_video_url: form.public_hero_video_url,
        public_showreel_urls: form.public_showreel_urls.split(/\n/).map((s) => s.trim()).filter(Boolean),
      };
      if (!handleLocked && form.public_handle && form.public_handle !== profile.public_handle) {
        body.public_handle = form.public_handle;
      }
      await api.patch("/orgs/mine/public-profile", body);
      toast.success("Public profile saved");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const inputStyle = {
    background: t.inputBg,
    border: `1px solid ${t.line}`,
    color: t.ink,
  };
  const shadowRgba = t.onDark ? "rgba(0,0,0,0.55)" : "rgba(30,27,24,0.15)";

  return (
    <div className="rounded-2xl p-5"
         style={{ background: t.surface,
                  border: `1px solid ${t.line}`,
                  boxShadow: `0 4px 20px -12px ${shadowRgba}` }}
         data-testid="app-owner-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase"
               style={{ color: t.accent }}>
            Your public URL
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <code className="text-[13px] px-2 py-1 rounded"
                  style={{ background: t.chipBg,
                           color: t.ink }}
                  data-testid="app-public-url">{publicUrl}</code>
            <button onClick={copy}
              className="p-2 rounded-md hover:bg-black/5"
              style={{ color: t.muted }}
              data-testid="app-copy-url">
              {copied ? <Check size={13} /> : <Copy size={13} />}
            </button>
            <Link to={`/agency/${profile.public_handle}`} target="_blank" rel="noreferrer"
              className="p-2 rounded-md hover:bg-black/5"
              style={{ color: t.muted }}
              data-testid="app-open-public">
              <ExternalLink size={13} />
            </Link>
          </div>
        </div>
        <button onClick={() => setOpen((s) => !s)}
          className="h-9 px-4 rounded-full text-[10.5px] tracking-[0.18em] uppercase font-semibold transition inline-flex items-center gap-1.5"
          style={open
            ? { background: t.ink, color: t.bg }
            : { border: `1px solid ${t.line}`, color: t.ink }}
          data-testid="app-toggle-editor">
          <Pencil size={12} /> {open ? "Close" : "Edit Profile"}
        </button>
      </div>

      {open && (
        <div className="mt-5 space-y-4">
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase"
                   style={{ color: t.muted }}>
              Statement · floating intro (≤ 800 chars)
            </label>
            <textarea value={form.public_statement} maxLength={800}
              onChange={(e) => setForm((s) => ({ ...s, public_statement: e.target.value }))}
              className="mt-1 w-full h-28 rounded-md px-3 py-2 text-sm resize-none focus:outline-none"
              style={inputStyle}
              placeholder="Tell the world what you do — the brands you've served, the stories you tell, the way you work."
              data-testid="app-statement-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase"
                     style={{ color: t.muted }}>Hero image URL</label>
              <input value={form.public_hero_image_url}
                onChange={(e) => setForm((s) => ({ ...s, public_hero_image_url: e.target.value }))}
                placeholder="https://…/hero.jpg"
                className="mt-1 w-full rounded-md h-10 px-3 text-sm focus:outline-none"
                style={inputStyle}
                data-testid="app-hero-image-input" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase"
                     style={{ color: t.muted }}>Hero video URL</label>
              <input value={form.public_hero_video_url}
                onChange={(e) => setForm((s) => ({ ...s, public_hero_video_url: e.target.value }))}
                placeholder="https://youtu.be/…"
                className="mt-1 w-full rounded-md h-10 px-3 text-sm focus:outline-none"
                style={inputStyle}
                data-testid="app-hero-video-input" />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase"
                   style={{ color: t.muted }}>
              Showreel URLs · one per line, up to 6
            </label>
            <textarea value={form.public_showreel_urls}
              onChange={(e) => setForm((s) => ({ ...s, public_showreel_urls: e.target.value }))}
              className="mt-1 w-full h-24 rounded-md px-3 py-2 text-sm resize-none font-mono focus:outline-none"
              style={inputStyle}
              placeholder={"https://youtu.be/…\nhttps://vimeo.com/…"}
              data-testid="app-showreel-input" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase"
                   style={{ color: t.muted }}>
              Handle · @{form.public_handle || profile.public_handle}
              {handleLocked && <span className="ml-2" style={{ color: t.accent }}>
                (locked — one-time change already used)
              </span>}
            </label>
            <input value={form.public_handle} disabled={handleLocked}
              onChange={(e) => setForm((s) => ({ ...s, public_handle: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") }))}
              className="mt-1 w-full rounded-md h-10 px-3 text-sm disabled:opacity-50 focus:outline-none"
              style={inputStyle}
              data-testid="app-handle-input" />
            {!handleLocked && (
              <div className="mt-1 text-[10.5px]" style={{ color: t.muted }}>
                You can change your handle ONCE. Choose carefully — this becomes your public URL forever.
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <button onClick={save} disabled={busy}
              className="h-10 px-6 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold disabled:opacity-60 inline-flex items-center gap-2 transition"
              style={{ background: t.accent, color: t.ctaText }}
              onMouseOver={(e) => { if (!busy) e.currentTarget.style.background = t.ink; }}
              onMouseOut={(e)  => { if (!busy) e.currentTarget.style.background = t.accent; }}
              data-testid="app-save">
              <Save size={12} /> {busy ? "Saving…" : "Save Profile"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
